import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_WORKFLOW_LIBRARY_ASSETS } from '@/lib/constants/api'
import getWorkflowLibraryAssetModel from './models/workflowLibraryAsset'
import updateStatus from '@/store/helpers/updateStatus'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const workflowLibraryAsset = {
  namespaced: true,
  state: {
    ...getWorkflowLibraryAssetModel()
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    clearWorkflowLibraryAsset: (state) => {
      const model = getWorkflowLibraryAssetModel()
      Object.keys(model).forEach(key => { state[key] = model[key] })
    },
    setWorkflowLibraryAsset: (state, asset) => {
      state.id = asset.id
      state.name = asset.name
      state.fileName = asset.fileName
      state.contentType = asset.contentType
      state.s3Key = asset.s3Key
      state.isActive = asset.isActive
      state.createdBy = asset.createdBy
      state.updatedBy = asset.updatedBy
      state.createdAt = asset.createdAt
      state.updatedAt = asset.updatedAt
    }
  },
  actions: {
    // Create is a multipart upload -- name/file only, matches WorkflowLibraryAssetUploadRequest.
    save (context, { name, file }) {
      return api.doPostFile({ path: PATH_WORKFLOW_LIBRARY_ASSETS, file, fields: { Name: name } })
    },
    // Rename/deactivate only -- S3Key/FileName/ContentType are set once at upload time.
    update ({ state }) {
      const { id, name, isActive } = state
      return api.doPut({ path: PATH_WORKFLOW_LIBRARY_ASSETS, data: { id, name, isActive } })
    },
    getWorkflowLibraryAsset: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading asset, please wait...' })

      return api.doGet({ path: `${PATH_WORKFLOW_LIBRARY_ASSETS}/${id}` })
        .then(json => json.data)
        .then(asset => {
          commit('setWorkflowLibraryAsset', asset)
          commit('updateStatus', { status: COMPLETE })
          return asset
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    uploadWorkflowLibraryAsset: ({ commit, dispatch }, { name, file }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Uploading asset, please wait...' })

      return api.doPostFile({ path: PATH_WORKFLOW_LIBRARY_ASSETS, file, fields: { Name: name } })
        .then(response => {
          if (!response.data) {
            if (response.errorMessage) {
              throw new Error(response.errorMessage)
            }
            throw new Error('Unable to upload asset.')
          }

          commit('setWorkflowLibraryAsset', response.data)
          commit('updateStatus', { status: COMPLETE })
          notifySuccess(dispatch, 'Asset uploaded')

          return response
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, error || 'Unable to upload asset. Try again later')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveWorkflowLibraryAsset: ({ dispatch }) => {
      showProgressModal({ dispatch, message: 'Saving asset, please wait...' })
      return dispatch('update').then(response => {
        if (!response.data) {
          if (response.errorMessage) {
            throw new Error(response.errorMessage)
          }
          throw new Error('Unable to save asset.')
        }
        notifySuccess(dispatch, 'Asset saved')
        return response
      }).catch((error) => {
        notifyError(dispatch, error || 'Unable to save asset. Try again later')
        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearWorkflowLibraryAsset: ({ commit }) => {
      commit('clearWorkflowLibraryAsset')
      return commit('updateStatus', { status: NOT_STARTED })
    }
  }
}

export default workflowLibraryAsset
